import { bytes32Schema, nonzeroAddressSchema } from '@hoodmorning/contracts';
import { getAddress, zeroAddress, toEventSelector } from 'viem';
import type { extractOwnerOperationExecutionLogs } from './operationExecutionLogs.js';

type Segment = ReturnType<typeof extractOwnerOperationExecutionLogs>;
const transferTopic = toEventSelector('Transfer(address,address,uint256)');

/** Narrow structural normalization, NOT balance evidence or a financial projection.
 * supportedTokens must originate from trusted server/release configuration, never
 * a request-supplied token list in production. Address validation is not proof of
 * token code, truthful Transfer semantics, canonicality or beneficial ownership.
 * Those independent historical source checks are required before publishing flows.
 */
export function normalizeVaultMovementCandidates(segment: Segment, supportedTokens: readonly string[]) {
  const vault = getAddress(nonzeroAddressSchema.parse(segment.sender));
  bytes32Schema.parse(segment.userOperationHash);
  const supported = new Set(supportedTokens.map(token => getAddress(nonzeroAddressSchema.parse(token)).toLowerCase()));
  if (supported.size !== supportedTokens.length) throw new Error('Duplicate supported token');
  if (segment.source !== 'direct-entrypoint-0.7-handleOps-structural-segment'
    || segment.verification !== 'canonicality-code-token-semantics-and-accounting-required'
    || typeof segment.success !== 'boolean') throw new Error('Unsupported execution segment');
  if (!segment.success && segment.candidateLogs.length !== 0) throw new Error('Failed operation contains candidate logs');
  if (!Number.isSafeInteger(segment.executionStartLogIndex) || segment.executionStartLogIndex < 0
    || !Number.isSafeInteger(segment.userOperationLogIndex)
    || segment.userOperationLogIndex <= segment.executionStartLogIndex) throw new Error('Invalid execution segment boundaries');
  let previous = segment.executionStartLogIndex;
  const identities = new Set<string>();
  return segment.candidateLogs.flatMap(log => {
    if (log.removed || log.logIndex === null || !Number.isSafeInteger(log.logIndex)
      || log.logIndex <= previous || log.logIndex >= segment.userOperationLogIndex
      || log.blockNumber === null || log.blockNumber < 0n || log.transactionIndex === null
      || !Number.isSafeInteger(log.transactionIndex) || log.transactionIndex < 0) throw new Error('Invalid candidate log identity');
    previous = log.logIndex;
    const blockHash = bytes32Schema.parse(log.blockHash);
    const transactionHash = bytes32Schema.parse(log.transactionHash);
    const identity = `${blockHash.toLowerCase()}:${transactionHash.toLowerCase()}:${log.transactionIndex}:${log.blockNumber}`;
    identities.add(identity);
    if (identities.size !== 1) throw new Error('Mixed candidate receipt identities');
    const token = getAddress(nonzeroAddressSchema.parse(log.address));
    if (!supported.has(token.toLowerCase()) || log.topics[0]?.toLowerCase() !== transferTopic) return [];
    // Exact ERC20 layout excludes ERC721's same-signature four-topic event and
    // noncanonical address padding. Never silently discard malformed supported Transfer.
    if (log.topics.length !== 3 || !/^0x[0-9a-fA-F]{64}$/.test(log.data)
      || !log.topics.slice(1).every(topic => /^0x0{24}[0-9a-fA-F]{40}$/.test(topic))) throw new Error('Malformed supported-token Transfer');
    const from = getAddress(`0x${log.topics[1]!.slice(26)}`);
    const to = getAddress(`0x${log.topics[2]!.slice(26)}`);
    if (from === to || (from !== vault && to !== vault)) return [];
    const direction = from === vault ? 'debit' as const : 'credit' as const;
    return [{ schemaVersion: 1 as const, chainId: 4663 as const, vault, token, direction,
      amount: BigInt(log.data).toString(), counterparty: direction === 'debit' ? to : from,
      transferKind: from === zeroAddress ? 'mint' as const : to === zeroAddress ? 'burn' as const : 'transfer' as const,
      userOperationHash: segment.userOperationHash, blockHash, blockNumber: log.blockNumber.toString(),
      transactionHash, transactionIndex: log.transactionIndex, logIndex: log.logIndex,
      verification: 'untrusted-transfer-candidate-requires-canonical-token-balance-semantics' as const }];
  });
}
